import { useState } from "react";
import {
  DAYS, FULL_DAYS, PERIODS, PERIOD_ICONS, PERIOD_TIMES_DISPLAY,
  Counter, CopyMenu,
} from "./shared";

export default function Step3RolesStaffing({ data, setData, isMobile }) {
  const [sel, setSel] = useState(() => {
    // Default to first open day
    const firstOpen = data.dayData.findIndex(d => d.isOpen);
    return firstOpen >= 0 ? firstOpen : 0;
  });
  const [cardKey, setCardKey] = useState(0);
  const [showCopy, setShowCopy] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleType, setNewRoleType] = useState("FOH");
  const [newRoleColor, setNewRoleColor] = useState("#3D5A80");
  const roles = data.roles;
  const staffing = data.staffing;
  const activeRoles = roles.filter(r => r.active);

  const CUSTOM_COLORS = ["#C44569", "#3D5A80", "#E07A5F", "#81B29A", "#F2CC8F", "#364958"];

  const addCustomRole = () => {
    const name = newRoleName.trim();
    if (!name) return;
    if (roles.find(r => r.name.toLowerCase() === name.toLowerCase())) return;
    setData(p => ({ ...p, roles: [...p.roles, { name, type: newRoleType, color: newRoleColor, active: true }] }));
    setNewRoleName("");
    setNewRoleType("FOH");
    setNewRoleColor(CUSTOM_COLORS[0]);
    setShowAddRole(false);
  };

  // Read hours data for each day
  const isDayOpen = (di) => data.dayData[di]?.isOpen;
  const getActivePeriodsForDay = (di) => {
    const dd = data.dayData[di];
    if (!dd || !dd.isOpen) return [];
    return PERIODS.filter(p => dd.periods[p]?.active);
  };

  const activePeriods = getActivePeriodsForDay(sel);
  const dayIsOpen = isDayOpen(sel);

  const toggleRole = i => {
    const u = [...roles];
    u[i] = { ...u[i], active: !u[i].active };
    setData(p => ({ ...p, roles: u }));
  };

  const setStaff = (k, v) => setData(p => ({ ...p, staffing: { ...p.staffing, [k]: v } }));

  const copyDay = targets => {
    const ns = { ...staffing };
    const srcPeriods = getActivePeriodsForDay(sel);
    targets.forEach(di => {
      // Only copy to open days
      if (di !== sel && isDayOpen(di)) {
        srcPeriods.forEach(pr => activeRoles.forEach(r => {
          ns[`${di}-${pr}-${r.name}`] = staffing[`${sel}-${pr}-${r.name}`] || 0;
        }));
      }
    });
    setData(p => ({ ...p, staffing: ns }));
  };

  const total = Object.values(staffing).reduce((a, b) => a + b, 0);
  const dayTotal = activePeriods.reduce((s, pr) => s + activeRoles.reduce((a, r) => a + (staffing[`${sel}-${pr}-${r.name}`] || 0), 0), 0);

  // Custom day selector that respects open/closed
  const DaySelectorWithHours = () => (
    <div style={{ display: "flex", gap: isMobile ? 8 : 6, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", padding: "4px 0 8px", marginBottom: 4, justifyContent: isMobile ? "flex-start" : "center" }}>
      {DAYS.map((day, di) => {
        const isSelected = di === sel;
        const isWeekend = di >= 5;
        const isOpen = isDayOpen(di);
        const dayPeriods = getActivePeriodsForDay(di);
        const dt = dayPeriods.reduce((s, pr) => s + activeRoles.reduce((a, r) => a + (staffing[`${di}-${pr}-${r.name}`] || 0), 0), 0);

        return (
          <button key={day} onClick={() => { if (isOpen) { setSel(di); setCardKey(k => k + 1); } }}
            style={{
              flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              padding: isMobile ? "10px 16px" : "10px 22px", borderRadius: 12,
              border: isSelected && isOpen ? "2px solid #1a1a1a" : "2px solid #EBEBEB",
              background: isSelected && isOpen ? "#1a1a1a" : isOpen ? "#FFF" : "#F5F5F4",
              cursor: isOpen ? "pointer" : "default",
              WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease",
              minWidth: isMobile ? 52 : 64, position: "relative",
              opacity: isOpen ? 1 : 0.45,
            }}>
            <span style={{
              fontSize: isMobile ? 13 : 14, fontWeight: 600, fontFamily: "'DM Sans',sans-serif",
              color: !isOpen ? "#BBB" : isSelected ? "#FFF" : isWeekend ? "#2D8F5E" : "#333",
              textTransform: "uppercase", letterSpacing: "0.06em",
              textDecoration: !isOpen ? "line-through" : "none",
            }}>{day}</span>
            {!isOpen && (
              <span style={{ fontSize: 9, fontWeight: 500, color: isSelected ? "rgba(255,255,255,0.5)" : "#CCC", fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.02em" }}>Closed</span>
            )}
            {isOpen && dt > 0 && !isSelected && (
              <>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#BBB", fontFamily: "'DM Sans',sans-serif" }}>{dt}</span>
                <div style={{ position: "absolute", bottom: 4, width: 4, height: 4, borderRadius: "50%", background: "#2D8F5E" }} />
              </>
            )}
            {isOpen && dt > 0 && isSelected && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.55)", fontFamily: "'DM Sans',sans-serif" }}>{dt}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: isMobile ? 20 : 28, textAlign: "center" }}>
        <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px", fontFamily: "'DM Sans',sans-serif", letterSpacing: "-0.02em" }}>Roles & Staffing</h1>
        <p style={{ fontSize: isMobile ? 13 : 15, color: "#888", margin: 0 }}>Select roles, then set how many you need each day</p>
      </div>

      {/* Role Selection */}
      <div style={{ background: "#FFF", borderRadius: 16, padding: isMobile ? "16px" : "20px 28px", marginBottom: isMobile ? 16 : 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid #EBEBEB" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Your Roles</h3>
          {activeRoles.length > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: "#2D8F5E", background: "#2D8F5E10", padding: "4px 10px", borderRadius: 6 }}>{activeRoles.length} selected</span>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {roles.map((r, i) => (
            <button key={r.name} onClick={() => toggleRole(i)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: isMobile ? "10px 14px" : "8px 16px", borderRadius: 20, border: r.active ? `2px solid ${r.color}` : "2px solid #E5E5E5", background: r.active ? `${r.color}0D` : "#FFF", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 13, fontWeight: 500, color: r.active ? r.color : "#888", WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: r.active ? r.color : "#CCC" }} />{r.name}<span style={{ fontSize: 10, color: r.active ? r.color + "99" : "#BBB", textTransform: "uppercase", letterSpacing: "0.05em" }}>{r.type}</span>
            </button>
          ))}
          {!showAddRole && (
            <button onClick={() => setShowAddRole(true)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: isMobile ? "10px 14px" : "8px 16px", borderRadius: 20, border: "2px dashed #DDD", background: "transparent", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 13, fontWeight: 600, color: "#BBB", WebkitTapHighlightColor: "transparent", transition: "all 0.15s ease" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#1a1a1a"; e.currentTarget.style.color = "#1a1a1a"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#DDD"; e.currentTarget.style.color = "#BBB"; }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Role
            </button>
          )}
        </div>
        {/* Inline add-role form */}
        {showAddRole && (
          <div style={{ marginTop: 12, padding: isMobile ? "14px" : "14px 18px", borderRadius: 14, background: "#F7F7F6", border: "1px solid #EBEBEB", display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, alignItems: isMobile ? "stretch" : "center" }}>
            <input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="e.g. Expeditor" autoFocus
              onKeyDown={e => { if (e.key === "Enter") addCustomRole(); if (e.key === "Escape") setShowAddRole(false); }}
              style={{ flex: 1, minWidth: 0, padding: "9px 14px", borderRadius: 10, border: "2px solid #E5E5E5", background: "#FFF", fontSize: 14, fontWeight: 500, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" }}
              onFocus={e => e.target.style.borderColor = "#1a1a1a"}
              onBlur={e => e.target.style.borderColor = "#E5E5E5"} />
            {/* FOH / BOH toggle */}
            <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid #E5E5E5", flexShrink: 0 }}>
              {["FOH", "BOH"].map(t => (
                <button key={t} onClick={() => setNewRoleType(t)} style={{ padding: "8px 14px", border: "none", background: newRoleType === t ? "#1a1a1a" : "#FFF", color: newRoleType === t ? "#FFF" : "#999", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", cursor: "pointer", transition: "all 0.15s ease", WebkitTapHighlightColor: "transparent" }}>{t}</button>
              ))}
            </div>
            {/* Color swatches */}
            <div style={{ display: "flex", gap: 5, flexShrink: 0, alignItems: "center" }}>
              {CUSTOM_COLORS.map(c => (
                <button key={c} onClick={() => setNewRoleColor(c)} style={{ width: 22, height: 22, borderRadius: "50%", background: c, border: newRoleColor === c ? "2px solid #1a1a1a" : "2px solid transparent", cursor: "pointer", transition: "border-color 0.15s ease", padding: 0, WebkitTapHighlightColor: "transparent", boxSizing: "border-box" }} />
              ))}
            </div>
            {/* Actions */}
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button onClick={addCustomRole} disabled={!newRoleName.trim()} style={{ padding: "8px 18px", borderRadius: 10, border: "none", background: newRoleName.trim() ? "#1a1a1a" : "#E5E5E5", color: newRoleName.trim() ? "#FFF" : "#999", fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", cursor: newRoleName.trim() ? "pointer" : "default", transition: "all 0.15s ease", WebkitTapHighlightColor: "transparent" }}>Add</button>
              <button onClick={() => { setShowAddRole(false); setNewRoleName(""); }} style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #E5E5E5", background: "#FFF", color: "#999", fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans',sans-serif", cursor: "pointer", WebkitTapHighlightColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Staffing Section */}
      {activeRoles.length > 0 && <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "0 4px" }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", margin: 0, fontFamily: "'DM Sans',sans-serif" }}>
            {FULL_DAYS[sel]}
            {dayIsOpen && dayTotal > 0 && <span style={{ fontSize: 13, fontWeight: 400, color: "#999", marginLeft: 8 }}>{dayTotal} staff</span>}
            {!dayIsOpen && <span style={{ fontSize: 13, fontWeight: 500, color: "#E85D3A", marginLeft: 8 }}>Closed</span>}
          </h3>
          {dayIsOpen && <CopyMenu label={DAYS[sel]} onCopy={copyDay} show={showCopy} setShow={setShowCopy} isMobile={isMobile} dayData={data.dayData} selectedDay={sel} />}
        </div>

        <DaySelectorWithHours />

        {/* Closed day message */}
        {!dayIsOpen && (
          <div style={{ background: "#FFF", borderRadius: 16, padding: "40px 20px", border: "1px solid #EBEBEB", textAlign: "center", marginTop: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>&#x26D4;</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif", marginBottom: 4 }}>{FULL_DAYS[sel]} is closed</div>
            <div style={{ fontSize: 13, color: "#BBB", fontFamily: "'DM Sans',sans-serif" }}>Go back to Hours & Periods to open this day</div>
          </div>
        )}

        {/* No active periods message */}
        {dayIsOpen && activePeriods.length === 0 && (
          <div style={{ background: "#FFF", borderRadius: 16, padding: "40px 20px", border: "1px solid #EBEBEB", textAlign: "center", marginTop: 12 }}>
            <div style={{ fontSize: 14, color: "#BBB", fontFamily: "'DM Sans',sans-serif" }}>No meal periods are active for {FULL_DAYS[sel]}. Enable them on the Hours & Periods step.</div>
          </div>
        )}

        {/* Period Cards — only active periods */}
        {dayIsOpen && activePeriods.length > 0 && (
          <div key={cardKey} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 12 : 16, marginTop: 12, animation: "fadeSlide 0.25s ease-out" }}>
            {activePeriods.map(period => {
              const pt = activeRoles.reduce((s, r) => s + (staffing[`${sel}-${period}-${r.name}`] || 0), 0);
              return (
                <div key={period} style={{ background: "#FFF", borderRadius: 16, padding: isMobile ? "14px 16px" : "18px 22px", border: "1px solid #EBEBEB", boxShadow: "0 1px 4px rgba(0,0,0,0.03)", flex: isMobile ? "none" : "1 1 0", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #F3F3F3" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: "#F7F7F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{PERIOD_ICONS[period]}</div>
                      <div>
                        <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif" }}>{period}</div>
                        <div style={{ fontSize: 11, color: "#AAA", fontFamily: "'DM Sans',sans-serif" }}>{PERIOD_TIMES_DISPLAY[period]}</div>
                      </div>
                    </div>
                    {pt > 0 && <div style={{ background: "#F0F0F0", borderRadius: 8, padding: "5px 12px", fontSize: 13, fontWeight: 700, color: "#1a1a1a", fontFamily: "'DM Sans',sans-serif" }}>{pt}</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 8 : 6 }}>
                    {activeRoles.map(role => {
                      const k = `${sel}-${period}-${role.name}`, v = staffing[k] || 0;
                      return (
                        <div key={role.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "8px 10px" : "6px 10px", borderRadius: 10, background: v > 0 ? `${role.color}06` : "#FAFAFA" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 8 }}>
                            <div style={{ width: 4, height: 24, borderRadius: 2, background: v > 0 ? role.color : "#E5E5E5" }} />
                            <span style={{ fontSize: isMobile ? 15 : 14, color: v > 0 ? "#333" : "#AAA", fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>{role.name}</span>
                          </div>
                          <Counter value={v} onChange={nv => setStaff(k, nv)} color={role.color} isMobile={isMobile} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary */}
        <div style={{ background: "#FFF", borderRadius: 16, padding: isMobile ? "14px 16px" : "18px 28px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid #EBEBEB", display: "flex", alignItems: "center", justifyContent: "space-around", marginTop: isMobile ? 16 : 24 }}>
          {[{ l: "Shifts / Week", v: total }, { l: "Roles", v: activeRoles.length }, { l: "Avg / Day", v: total > 0 ? Math.round(total / 7) : 0 }].map(({ l, v }) =>
            <div key={l} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "#999", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'DM Sans',sans-serif" }}>{l}</div>
              <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: "#1a1a1a", marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>{v}</div>
            </div>
          )}
        </div>
      </>}
    </div>
  );
}
